/**
 * Roster — Load Balance Board.
 *
 * A redesign of the roster that leads with FAIRNESS AND CAPACITY rather than a
 * grid of hours: three plant metrics, then one row per worker ranked by load,
 * with the hour timeline compressed into a lane inside each row. Day, Week and
 * Worker share one shell.
 *
 * Built to the Wilya design handoff. Two deliberate departures from the mock,
 * both because the mock ran on fixtures and this runs on the plant's data:
 *   - the hour window follows the actual assignments instead of a fixed 05:00–14:00
 *   - "role" is the work a person actually did that day, so cross-skilled
 *     workers read "Electrical · Mechanical" instead of a single stored label
 *
 * This file is the composition: state and wiring only. The derivations live
 * in controller/, the pure maths in utils/, the pieces on screen in ui/.
 */
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Snackbar } from "@mui/material";
import { addDays, format, startOfWeek } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";

import { LaborStandard } from "../../../../services/labor-standards-api";
import { AvailabilityRow, MoveGap, RosterAssignment, SuggestCandidate } from "../../../../services/roster-assignments-api";
import rosterAssignmentsApi from "../../../../services/roster-assignments-api";
import { ShiftInstance } from "../../../../services/shifts-api";
import { apiErrorDetails } from "../../../../utils/api-error";
import useIsMobileLayout from "../../../../utils/use-is-mobile-layout";
import { ManufacturingOrder } from "../../production-plan/utils/calculations";
import { RosterReportRow, rosterReportRows } from "../../utils/reports";
import { localDay } from "../../utils/time";
import { downloadSheet, ReportColumn } from "../../utils/xlsx-report"; // eslint-disable-line sort-imports
import { useBoardDerivations } from "./controller/use-board-derivations";
import { useCoverDrafts } from "./controller/use-cover-drafts";
import { useFillRange } from "./controller/use-fill-range";
import { useManualMoves } from "./controller/use-manual-moves";
import { useStandingPins } from "./controller/use-standing-pins";
import { BoardScreen, BoardView, FlexRequestRow, PinEnd } from "./types";
import BoardHeader from "./ui/board-header";
import CrewBoard from "./ui/crew-board";
import FillPicker, { FillRequest } from "./ui/fill-picker";
import FillRangeDialog from "./ui/fill-range-dialog";
import FlexRequests from "./ui/flex-requests";
import FooterLegend from "./ui/footer-legend";
import GapBoard from "./ui/gap-board";
import LineView from "./ui/line-view";
import { ConfirmMoveDialog, GapPrompt, HowDialog, MoveDragLayer, MoveToast, PinAskDialog, PlaceAskDialog, RemoveDialog, SplitDialog, UntilDialog } from "./ui/manual-controls";
import MetricsBand from "./ui/metrics-band";
import MoveBanner from "./ui/move-banner";
import PhoneRoster from "./ui/phone-roster";
import RosterPrintSheet from "./ui/roster-print-sheet";
import { KnownJob } from "./ui/standing-crew-panel";
import TimelineView from "./ui/timeline-view";
import WeekLineView, { PinEndOnDay } from "./ui/week-line-view";
import { dayPeople } from "./utils/day-people";
import { workerDropState } from "./utils/drop-state";
import { ShiftNumber } from "./utils/gap-derive";
import { stepTitle } from "./utils/lane-rows";
import { confirmLeaveWithDrafts } from "./utils/pending-drafts";
import { pinnedUntil } from "./utils/pin-range";
import { buildTimeline } from "./utils/timeline";
import { fmtHoursShort, lbb } from "./utils/tokens";
import { MoveTarget } from "./utils/undo";

const ROSTER_COLUMNS: ReportColumn<RosterReportRow>[] = [
	{ header: "Date", key: "date", width: 12 },
	{ header: "Work center", key: "workcenter", width: 24 },
	{ header: "Product", key: "product", width: 40 },
	{ header: "Batch", key: "batch", width: 10 },
	{ header: "Job", key: "job", width: 24 },
	{ header: "Person", key: "person", width: 26 },
	{ header: "Start", key: "start", width: 17 },
	{ header: "End", key: "end", width: 17 },
	{ header: "Hours", key: "hours", width: 8 },
	{ header: "Placed", key: "placedBy", width: 10 },
	{ header: "Notes", key: "notes", width: 40 },
];

export interface LoadBalanceBoardProps {
	weekAssignments: RosterAssignment[];
	/** The week's blocks at every plant (the people list shows who is lent to a sister plant). */
	allAssignments?: RosterAssignment[];
	manufacturingOrders: ManufacturingOrder[];
	weekStartDate: Date;
	selectedDate: Date;
	onDateChange: (date: Date) => void;
	locationName?: string;
	/** Total labor hours the day's orders require, for the coverage metric. */
	requiredHours?: number;
	onAllocate?: () => void;
	/** Allocate the whole visible week (the Week views' button, and the re-allocation after a standing pin). */
	onAllocateWeek?: () => void | Promise<void>;
	allocating?: boolean;
	/** Phase id -> operation name, so blocks read "Bed cap" not "Mechanical". */
	operationNames?: Map<number, string>;
	/** Drives View 2's required side — demand comes from the day's standards. */
	laborStandards?: LaborStandard[];
	/** The week's shift instances: a worker's cap is the length of THEIR shift. */
	shifts?: ShiftInstance[];
	/**
	 * Who is out (approved time off, training, restricted duty, reassigned,
	 * absent) or asking to be (a pending request) on the week's shifts. Every
	 * view marks the person's chip so the supervisor can replace them.
	 */
	availability?: AvailabilityRow[];
	onOpenFlexScheduler?: () => void;
	/**
	 * Hands the drafted batch to the flex scheduler. Left to the caller because
	 * publishing SENDS OFFERS TO REAL WORKERS — the board should not decide on
	 * its own that that is safe.
	 */
	onPublishDrafts?: (requests: FlexRequestRow[]) => void | Promise<void>;
	/** The plant, for every manual-control call. */
	locationId: number;
	/** The plant's work centers in display order — the lanes of the Line view. */
	workcenters?: string[];
	/** The company's jobs, for the optional role on a standing pin. */
	jobs?: KnownJob[];
	/** Refetch the assignments after a move / fill / unpin. */
	onAssignmentsChanged: () => void | Promise<void>;
	/** The plan itself changed (orders moved): reload the whole week. */
	onPlanChanged?: () => void | Promise<void>;
	/**
	 * Handed back to the caller so a reload that bypasses the board (Allocate,
	 * the initial fetch) can drop the move-target cache too.
	 */
	invalidateTargetsRef?: React.MutableRefObject<(() => void) | null>;
	/** Shift roster: lines × shifts with each shift's own crew, batches per day (the Shift Roster tab). */
	layout?: "timeline" | "crew";
}

/** Off-screen but read by assistive tech. */
const VISUALLY_HIDDEN: React.CSSProperties = {
	position: "absolute",
	width: 1,
	height: 1,
	margin: -1,
	padding: 0,
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap",
	border: 0,
};

const NO_STANDARDS: LaborStandard[] = [];
const NO_SHIFTS: ShiftInstance[] = [];
const NO_AVAILABILITY: AvailabilityRow[] = [];
const NO_WORKCENTERS: string[] = [];
const NO_JOBS: KnownJob[] = [];

function LoadBalanceBoard({
	weekAssignments,
	allAssignments,
	manufacturingOrders,
	weekStartDate,
	selectedDate,
	onDateChange,
	locationName = "",
	requiredHours = 0,
	onAllocate,
	onAllocateWeek,
	allocating,
	operationNames,
	laborStandards = NO_STANDARDS,
	shifts = NO_SHIFTS,
	availability = NO_AVAILABILITY,
	onOpenFlexScheduler,
	onPublishDrafts,
	locationId,
	workcenters = NO_WORKCENTERS,
	jobs = NO_JOBS,
	onAssignmentsChanged,
	onPlanChanged,
	invalidateTargetsRef,
	layout = "timeline",
}: LoadBalanceBoardProps) {
	const [screen, setScreen] = useState<BoardScreen>("util");
	const [view, setView] = useState<BoardView>("Week");
	const [shift, setShift] = useState<ShiftNumber>(1);
	const [gapSel, setGapSel] = useState<string | null>(null);
	const [selected, setSelected] = useState<number | null>(null);
	const [fillRequest, setFillRequest] = useState<FillRequest | null>(null);
	// What a screen reader hears: the pick-up, then the outcome. Sighted users
	// get the same two things from the MOVING banner and the toast.
	const [announcement, setAnnouncement] = useState("");
	// Print: the sheet mounts, the dialog opens on the next frame, and the
	// sheet unmounts when the dialog closes (afterprint) so it never lingers.
	const [printing, setPrinting] = useState(false);
	const printWeek = useCallback(() => setPrinting(true), []);
	const openDisplay = useCallback(() => {
		const query = new URLSearchParams({ plant: String(locationId), name: locationName });
		window.open(`/floor-display?${query.toString()}`, "_blank", "noopener");
	}, [locationId, locationName]);
	// The week's roster as a spreadsheet: any week, past ones too.
	const downloadWeek = useCallback(() => {
		const label = format(weekStartDate, "yyyy-MM-dd");
		void downloadSheet(`roster-${locationName || "plant"}-${label}.xlsx`, `Roster ${label}`, ROSTER_COLUMNS, rosterReportRows(weekAssignments, manufacturingOrders));
	}, [weekStartDate, locationName, weekAssignments, manufacturingOrders]);
	useEffect(() => {
		if (!printing) {
			return undefined;
		}
		const done = () => setPrinting(false);
		window.addEventListener("afterprint", done);
		const frame = window.requestAnimationFrame(() => window.print());
		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener("afterprint", done);
		};
	}, [printing]);

	// Both sub-views lay the plan out by line: lanes per work center.
	const isWeek = view === "Week";
	const isLine = view === "Day";
	const isWeekLine = isWeek;

	// The week's standing pins, shared by the crew panel and the cards.
	const standing = useStandingPins(locationId, weekStartDate);
	// Plant names, for people from a sister plant (location cluster) on this plant's roster.
	const allLocations = useSelector((state: { fullTimeShifts?: { filterData?: { allLocations?: { id: number; name: string }[] } } }) => state.fullTimeShifts?.filterData?.allLocations);
	const plantNames = useMemo<Record<number, string>>(
		() => Object.fromEntries((allLocations ?? []).map((location) => [location.id, location.name])),
		[allLocations]
	);

	// The step a block is on, for the remove confirm and the replace picker.
	const stepTitleFor = useCallback(
		(workOrderId: number | undefined): string => {
			const order = workOrderId !== undefined ? manufacturingOrders.find((mo) => mo.id === workOrderId) : undefined;
			return order ? stepTitle(order) : `step #${workOrderId ?? "?"}`;
		},
		[manufacturingOrders]
	);

	// "Fill range…": months at a time, on the server; the board reloads when it is done.
	const reloadAfterFill = useCallback(() => onAssignmentsChanged(), [onAssignmentsChanged]);
	const fillRange = useFillRange(locationId, reloadAfterFill);
	const [fillRangeOpen, setFillRangeOpen] = useState(false);
	const openFillRange = useCallback(() => setFillRangeOpen(true), []);
	const closeFillRange = useCallback(() => setFillRangeOpen(false), []);

	// "Replace…" on a card: the fill picker for the block's own step / phase / window.
	const replaceRequest = useCallback(
		(block: RosterAssignment) => {
			setFillRequest({
				workOrderId: block.workOrderId,
				phaseId: block.laborStandardPhaseId ?? null,
				start: block.startTime,
				end: block.endTime,
				jobRoleId: block.jobRoleId ?? null,
				jobRoleName: block.jobRoleName,
				title: `${stepTitleFor(block.workOrderId)}${block.workstation ? ` · ${block.workstation}` : ""} · ${block.jobRoleName}`,
				replace: { assignmentId: block.id, workerName: block.workerName, stepTitle: stepTitleFor(block.workOrderId) },
			});
		},
		[stepTitleFor]
	);

	// Manual control: drag / click moves, undo, pins. Saved at once; the
	// board redraws from the refetched rows.
	const manual = useManualMoves({
		locationId,
		assignments: weekAssignments,
		weekStart: weekStartDate,
		selectedDate,
		selectedId: selected,
		onChanged: onAssignmentsChanged,
		onPinsChanged: standing.reload,
		onReplaceRequest: replaceRequest,
		stepTitleFor,
		onReallocateWeek: onAllocateWeek,
	});
	useEffect(() => {
		if (!invalidateTargetsRef) {
			return undefined;
		}
		invalidateTargetsRef.current = manual.invalidateTargets;
		return () => {
			invalidateTargetsRef.current = null;
		};
	}, [invalidateTargetsRef, manual.invalidateTargets]);

	// Allocate applies the drafted placements first; if one fails to save the
	// drafts stay on the board and the allocation is held back.
	const allocateWeekClick = useCallback(() => {
		void (async () => {
			if (!(await manual.commitDraftPins())) {
				return;
			}
			await onAllocateWeek?.();
		})();
	}, [manual, onAllocateWeek]);
	const allocateDayClick = useCallback(() => {
		void (async () => {
			if (!(await manual.commitDraftPins())) {
				return;
			}
			onAllocate?.();
		})();
	}, [manual, onAllocate]);

	// Escape puts a click-selected block back down.
	useEffect(() => {
		if (selected === null) {
			return undefined;
		}
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setSelected(null);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [selected]);
	const showFlex = useCallback(() => setScreen("flex"), []);
	const cover = useCoverDrafts({ onPublishDrafts, onPublished: showFlex });

	const derived = useBoardDerivations({
		weekAssignments,
		manufacturingOrders,
		laborStandards,
		shifts,
		availability,
		weekStartDate,
		selectedDate,
		operationNames,
		workcenters,
		requiredHours,
		isWeek,
		shift,
		drafts: cover.drafts,
		published: cover.published,
		selected,
	});
	const { clock, span, cap, weekCap, selectedBlock } = derived;

	// Clear the shown day or week: a confirm with the counts, manual blocks kept unless ticked.
	const [clearAsk, setClearAsk] = useState<{ from: string; to: string; label: string; allocated: number; manual: number } | null>(null);
	const [clearManual, setClearManual] = useState(false);
	const [clearing, setClearing] = useState(false);
	const [notice, setNotice] = useState<{ message: string; severity: "success" | "error" } | null>(null);
	const openClear = useCallback(() => {
		const from = isWeek ? localDay(weekStartDate) : localDay(selectedDate);
		const to = isWeek ? localDay(addDays(weekStartDate, 6)) : from;
		const inRange = weekAssignments.filter((a) => {
			const day = localDay(new Date(a.startTime));
			return day >= from && day <= to;
		});
		setClearManual(false);
		setClearAsk({
			from,
			to,
			label: isWeek ? `the week of ${format(weekStartDate, "d MMM")}` : format(selectedDate, "EEEE d MMM"),
			allocated: inRange.filter((a) => !a.isManuallyAssigned).length,
			manual: inRange.filter((a) => a.isManuallyAssigned).length,
		});
	}, [isWeek, weekStartDate, selectedDate, weekAssignments]);
	const cancelClear = useCallback(() => setClearAsk(null), []);
	const toggleClearManual = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setClearManual(event.target.checked), []);
	const confirmClear = useCallback(() => {
		if (!clearAsk) {
			return;
		}
		const ask = clearAsk;
		setClearing(true);
		void (async () => {
			try {
				const result = await rosterAssignmentsApi.clearRange(locationId, ask.from, ask.to, clearManual);
				setClearAsk(null);
				setNotice({
					severity: "success",
					message: `${result.deleted} ${result.deleted === 1 ? "block" : "blocks"} removed from ${ask.label}${result.keptManual ? ` · ${result.keptManual} manual kept` : ""}. Drag names from the list to place people.`,
				});
				await onAssignmentsChanged();
			} catch (err) {
				setNotice({ severity: "error", message: apiErrorDetails(err, "The roster could not be cleared.").message });
			} finally {
				setClearing(false);
			}
		})();
	}, [clearAsk, clearManual, locationId, onAssignmentsChanged]);
	const dismissNotice = useCallback(() => setNotice(null), []);

	// The day view's people list: who is free, who is where, who is out today.
	const dayPeopleList = useMemo(
		() => dayPeople(derived.knownWorkers, weekAssignments, localDay(selectedDate)),
		[derived.knownWorkers, weekAssignments, selectedDate]
	);
	// The week as a timeline (default) or the classic day-column board.
	const [weekStyle, setWeekStyle] = useState<"timeline" | "classic">(() => {
		try {
			return window.localStorage.getItem("roster.weekStyle") === "classic" ? "classic" : "timeline";
		} catch {
			return "timeline";
		}
	});
	const switchWeekStyle = useCallback((next: "timeline" | "classic") => {
		setWeekStyle(next);
		try {
			window.localStorage.setItem("roster.weekStyle", next);
		} catch {
			// Not remembered; the board still switches.
		}
	}, []);
	const showClassic = useCallback(() => switchWeekStyle("classic"), [switchWeekStyle]);
	const showTimeline = useCallback(() => switchWeekStyle("timeline"), [switchWeekStyle]);
	const timeline = useMemo(() => (derived.weekLanes ? buildTimeline(derived.weekLanes) : null), [derived.weekLanes]);
	const draftedOnByWorker = useMemo(() => new Map(manual.draftPins.map((d) => [String(d.workerId), d.workstation])), [manual.draftPins]);

	// The standing pin covering a worker on a work center on the shown day, if any.
	const pinEndOnDay = useCallback<PinEnd>(
		(workerId, workstation) => pinnedUntil(standing.pins, workerId, workstation, selectedDate),
		[standing.pins, selectedDate]
	);
	// The same for any day of the week (week · by line).
	const pinEndOn = useCallback<PinEndOnDay>(
		(workerId, workstation, day) => pinnedUntil(standing.pins, workerId, workstation, day),
		[standing.pins]
	);

	const movingName = selectedBlock?.workerName ?? (manual.dragging ? manual.dragging.label : null);
	const movingKey = selectedBlock?.id ?? manual.dragging?.assignmentId ?? null;
	useEffect(() => {
		if (movingKey !== null && movingName) {
			setAnnouncement(`Moving ${movingName}; choose a line or a person`);
		}
	}, [movingKey, movingName]);
	useEffect(() => {
		if (manual.toast) {
			setAnnouncement([manual.toast.message, manual.toast.detail].filter(Boolean).join(" "));
		}
	}, [manual.toast]);

	// A gap's "Fill from bench" and an empty slot's "+ Assign" open the same picker.
	const fillGap = useCallback(
		(gap: MoveGap) => {
			const order = manufacturingOrders.find((mo) => mo.id === gap.workOrderId);
			setFillRequest({
				workOrderId: gap.workOrderId,
				phaseId: gap.phaseId,
				start: gap.startTime,
				end: gap.endTime,
				jobRoleId: null,
				jobRoleName: gap.jobRole,
				title: `${order?.productName || order?.sku || `WO #${gap.workOrderId}`} · ${gap.workstation} · ${gap.jobRole}`,
			});
			manual.dismissGap();
		},
		[manufacturingOrders, manual]
	);
	const fillPendingGap = useCallback(() => {
		if (manual.gap) {
			fillGap(manual.gap);
		}
	}, [manual.gap, fillGap]);
	const closeFill = useCallback(() => setFillRequest(null), []);
	// Replace: the hand-over move to the chosen candidate — the same path a
	// drop on a name takes, so a 409 (unqualified / overtime) opens the same confirm.
	const replaceWith = useCallback(
		(assignmentId: number, candidate: SuggestCandidate) => {
			manual.requestMove(
				assignmentId,
				{ kind: "worker", workerId: candidate.workerId, workerName: candidate.workerName },
				{ wholeStep: false }
			);
			setFillRequest(null);
		},
		[manual]
	);

	const clearSelection = useCallback(() => setSelected(null), []);
	// A step header in week · by line: that day, by line.
	const openDayLine = useCallback(
		(date: Date) => {
			onDateChange(date);
			setView("Day");
			setSelected(null);
		},
		[onDateChange]
	);
	// Leaving the week with drafts pending asks first; a yes drops the drafts.
	const leaveWeekFor = useCallback(
		(next: Date) => {
			if (localDay(startOfWeek(next, { weekStartsOn: 1 })) !== localDay(weekStartDate) && !confirmLeaveWithDrafts()) {
				return;
			}
			manual.clearDraftPins();
			onDateChange(next);
		},
		[onDateChange, weekStartDate, manual]
	);
	const stepDate = useCallback(
		(direction: -1 | 1) => leaveWeekFor(addDays(selectedDate, direction * (isWeek ? 7 : 1))),
		[leaveWeekFor, selectedDate, isWeek]
	);
	const goToday = useCallback(() => leaveWeekFor(new Date()), [leaveWeekFor]);

	// Phones get a read-only day list instead of the drag-and-drop board.
	const isMobileLayout = useIsMobileLayout();
	useEffect(() => {
		if (isMobileLayout && view !== "Day") setView("Day");
	}, [isMobileLayout, view]);
	const previousDay = useCallback(() => leaveWeekFor(addDays(selectedDate, -1)), [leaveWeekFor, selectedDate]);
	const nextDay = useCallback(() => leaveWeekFor(addDays(selectedDate, 1)), [leaveWeekFor, selectedDate]);
	const freeToday = useMemo(() => dayPeopleList.filter((person) => person.state === "free").length, [dayPeopleList]);
	const pickShift = useCallback((next: ShiftNumber) => {
		setShift(next);
		setGapSel(null);
	}, []);
	const draftCover = useCallback(
		(key: string, method: Parameters<typeof cover.draft>[1]) => {
			cover.draft(key, method);
			setGapSel(null);
		},
		[cover]
	);
	const publishDrafts = useCallback(() => void cover.publish(derived.draftedRequests), [cover, derived.draftedRequests]);
	const openFlexScheduler = useCallback(() => onOpenFlexScheduler?.(), [onOpenFlexScheduler]);

	const plant = locationName ? ` · ${locationName.toUpperCase()}` : "";
	const rangeLabel = isWeek
		? `${format(weekStartDate, "EEE d MMM").toUpperCase()} – ${format(addDays(weekStartDate, 6), "EEE d MMM yyyy").toUpperCase()}${plant}`
		: `${format(selectedDate, "EEE d MMM yyyy").toUpperCase()}${plant} · ${String(clock.start).padStart(2, "0")}:00 – ${String(clock.end).padStart(2, "0")}:00`;

	const capNow = isWeek ? weekCap : cap;
	const capLabel = fmtHoursShort(capNow);

	if (isMobileLayout) {
		return (
			<PhoneRoster
				dateLabel={format(selectedDate, "EEE d MMM")}
				locationName={locationName}
				rows={derived.laneRows}
				utilPct={derived.metrics.utilPct}
				coveredPct={derived.gapSummary.coveredPct}
				overtimeHours={derived.metrics.overtimeHours}
				peopleCount={dayPeopleList.length}
				freeCount={freeToday}
				onPrevious={previousDay}
				onNext={nextDay}
				onToday={goToday}
			/>
		);
	}

	const boardFill = isWeek && onAllocateWeek ? allocateWeekClick : onAllocate ? allocateDayClick : undefined;

	return (
		<DndProvider backend={HTML5Backend}>
			{/* The board takes the width the page gives it (the roster's 24px gutters,
			    same as the Work Plan); below its floor it scrolls sideways rather than
			    squeezing. The timeline scrolls its own days, so it fits the screen and
			    only the classic board keeps the 1180px floor. */}
			<div
				data-fit-screen={weekStyle === "timeline" || layout === "crew" ? "" : undefined}
				style={{ padding: 0, overflowX: "auto", minWidth: 0 }}
			>
				<div
					style={{
						minWidth: weekStyle === "timeline" || layout === "crew" ? 860 : 1180,
						width: "100%",
						boxSizing: "border-box",
						background: lbb.paper,
						border: lbb.borderInk,
						borderRadius: 6,
						overflow: "hidden",
					}}
				>
					<BoardHeader
						rangeLabel={rangeLabel}
						screen={screen}
						view={view}
						shift={shift}
						isWeek={isWeek}
						onStep={stepDate}
						onToday={goToday}
						onView={setView}
						onShift={pickShift}
						onPrint={isWeek && derived.weekLanes ? printWeek : undefined}
						onDownload={isWeek ? downloadWeek : undefined}
						onDisplay={locationId ? openDisplay : undefined}
						onClear={screen === "util" ? openClear : undefined}
						onFill={layout === "crew" ? boardFill : undefined}
						onFillRange={layout === "crew" ? openFillRange : undefined}
						fillRangeLabel={fillRange.running && fillRange.progress ? `Filling ${fillRange.progress.done}/${fillRange.progress.total}` : undefined}
						fillLabel={allocating ? "Filling…" : manual.busy ? "Saving…" : isWeek ? "Fill open slots · week" : "Fill open slots"}
						filling={allocating || manual.busy}
					/>

					{layout !== "crew" && <MetricsBand
						screen={screen}
						onScreen={setScreen}
						isWeek={isWeek}
						metrics={derived.metrics}
						gapSummary={derived.gapSummary}
						gapRowCount={derived.gapRows.length}
						capLabel={capLabel}
						onAllocate={isWeek && onAllocateWeek ? allocateWeekClick : onAllocate ? allocateDayClick : undefined}
						allocating={allocating}
						busy={manual.busy}
						pendingPins={manual.draftPins.length}
						draftCount={cover.draftCount}
						published={cover.published}
						publishing={cover.publishing}
						onPublish={publishDrafts}
					/>}

					{/* ---- Gap left by the last move (the Line view shows it on the step itself) ---- */}
					{screen === "util" && manual.gap && !isLine && (
						<GapPrompt gap={manual.gap} onFill={fillPendingGap} onDismiss={manual.dismissGap} />
					)}

					{/* ---- Move banner (View 1 only) ---- */}
					{screen === "util" && selectedBlock && (
						<MoveBanner block={selectedBlock} isLine={isLine} onCancel={clearSelection} />
					)}

					{/* ---- Views ---- */}
					{screen === "util" && layout === "crew" && (isLine || isWeekLine) && timeline && locationId > 0 && (
						<CrewBoard
							timeline={timeline}
							weekStart={weekStartDate}
							locationId={locationId}
							zoom={isLine ? "day" : "week"}
							focusDate={selectedDate}
							selected={selected}
							onSelect={setSelected}
							moves={manual}
							onFill={setFillRequest}
							pinEnd={pinEndOn}
							onPlanChanged={onPlanChanged}
							assignments={allAssignments ?? weekAssignments}
							availability={derived.availabilityIndex}
						/>
					)}

					{screen === "util" && layout !== "crew" && isLine && weekStyle === "timeline" && timeline && (
						<TimelineView
							onClassic={showClassic}
							zoom="day"
							focusDate={selectedDate}
							timeline={timeline}
							weekStart={weekStartDate}
							workers={derived.knownWorkers}
							shifts={shifts}
							selected={selected}
							onSelect={setSelected}
							moves={manual}
							onFill={setFillRequest}
							pinEnd={pinEndOn}
							locationId={locationId}
							plantNames={plantNames}
						/>
					)}

					{screen === "util" && layout !== "crew" && isLine && weekStyle === "classic" && (
						<LineView
							dayIso={localDay(selectedDate)}
							people={dayPeopleList}
							draftedOn={draftedOnByWorker}
							rows={derived.laneRows}
							clock={clock}
							selected={selected}
							onSelect={setSelected}
							moves={manual}
							onFill={setFillRequest}
							onFillGap={fillGap}
							pinEnd={pinEndOnDay}
						/>
					)}

					{screen === "util" && layout !== "crew" && isWeekLine && weekStyle === "classic" && derived.weekLanes && (
						<>
							<div style={{ padding: "6px 16px", borderBottom: lbb.borderHair, fontFamily: lbb.fontUi, fontSize: 12, color: lbb.ink60 }}>
								<button type="button" onClick={showTimeline} style={{ border: "none", background: "none", color: lbb.brick, textDecoration: "underline", cursor: "pointer", font: "inherit", padding: 0 }}>
									Switch to the new timeline
								</button>
							</div>
							<WeekLineView
								lanes={derived.weekLanes}
								workers={derived.knownWorkers}
								shifts={shifts}
								selected={selected}
								onSelect={setSelected}
								moves={manual}
								onFill={setFillRequest}
								onOpenDay={openDayLine}
								pinEnd={pinEndOn}
							/>
						</>
					)}

					{screen === "util" && layout !== "crew" && isWeekLine && weekStyle === "timeline" && timeline && (
						<TimelineView
							onClassic={showClassic}
							zoom="week"
							timeline={timeline}
							weekStart={weekStartDate}
							workers={derived.knownWorkers}
							shifts={shifts}
							selected={selected}
							onSelect={setSelected}
							moves={manual}
							onFill={setFillRequest}
							pinEnd={pinEndOn}
							locationId={locationId}
							plantNames={plantNames}
							onOpenDay={openDayLine}
						/>
					)}

					{screen === "gaps" && (
						<GapBoard
							rows={derived.gapRows}
							shift={shift}
							drafts={cover.drafts}
							selected={gapSel}
							onSelect={setGapSel}
							onDraft={draftCover}
							shortBlocks={derived.gapSummary.shortBlocks}
							uncoveredHours={derived.gapSummary.uncoveredHours}
							pullSource={derived.pullSource}
						/>
					)}

					{screen === "flex" && (
						<FlexRequests
							rows={derived.draftedRequests}
							published={cover.published}
							dayLabel={format(selectedDate, "EEEE").toUpperCase()}
							onOpenFlexScheduler={openFlexScheduler}
						/>
					)}

					{screen === "util" && layout !== "crew" && (
						<FooterLegend isWeek={isWeek} isLine={isLine} isWeekLine={isWeekLine} capNow={capNow} capLabel={capLabel} />
					)}
				</div>

				<div role="status" aria-live="polite" style={VISUALLY_HIDDEN}>
					{announcement}
				</div>
				<MoveDragLayer shiftHeld={manual.shiftHeld} />
				<ConfirmMoveDialog moves={manual} />
				<UntilDialog moves={manual} />
				<RemoveDialog moves={manual} />
				<PinAskDialog moves={manual} />
				<PlaceAskDialog moves={manual} />
				<Dialog open={Boolean(clearAsk)} onClose={cancelClear} maxWidth="xs" fullWidth>
					<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Clear {clearAsk?.label}?</DialogTitle>
					<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
						<p style={{ margin: 0 }}>
							{clearAsk?.allocated ?? 0} allocated {clearAsk?.allocated === 1 ? "block goes" : "blocks go"}. Standing pins stay. You then place people by dragging names from the list.
						</p>
						{(clearAsk?.manual ?? 0) > 0 && (
							<FormControlLabel
								sx={{ mt: 1.5, "& .MuiFormControlLabel-label": { fontFamily: lbb.fontUi, fontSize: 14 } }}
								control={<Checkbox checked={clearManual} onChange={toggleClearManual} size="small" />}
								label={`Also remove the ${clearAsk?.manual} manual ${clearAsk?.manual === 1 ? "placement" : "placements"}`}
							/>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={cancelClear} disabled={clearing} autoFocus sx={{ color: lbb.ink60 }}>
							Keep it
						</Button>
						<Button onClick={confirmClear} disabled={clearing} variant="contained" sx={{ bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}>
							{clearing ? "Clearing…" : "Clear"}
						</Button>
					</DialogActions>
				</Dialog>
				<Snackbar open={Boolean(notice)} autoHideDuration={8000} onClose={dismissNotice} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
					<Alert onClose={dismissNotice} severity={notice?.severity ?? "success"} sx={{ fontFamily: lbb.fontUi }}>
						{notice?.message}
					</Alert>
				</Snackbar>
				<SplitDialog moves={manual} />
				<HowDialog moves={manual} />
				<MoveToast moves={manual} />
				{printing && derived.weekLanes && (
					<RosterPrintSheet lanes={derived.weekLanes} shifts={shifts} weekStart={weekStartDate} plantName={locationName} />
				)}
				<FillRangeDialog open={fillRangeOpen} onClose={closeFillRange} fill={fillRange} weekStart={weekStartDate} plantName={locationName} />
				<FillPicker
					request={fillRequest}
					locationId={locationId}
					onClose={closeFill}
					onAssigned={manual.changed}
					onReplace={replaceWith}
					availability={derived.availabilityIndex}
				/>
			</div>
		</DndProvider>
	);
}

export default LoadBalanceBoard;
